import React, { useEffect, useRef, useState } from "react";
import { withRouter } from "react-router-dom";

import { searchArticles } from "../../modules/search";
import { ArrowRightIcon, SearchIcon } from "./icons";

const SUGGESTION_LIMIT = 5;
const SUGGEST_AFTER_MS = 120;

let instanceCount = 0;

/**
 * Searches help articles (not the catalog -- that is the header's box).
 *
 * The spec makes the two look different on purpose, so a reader with both on
 * screen can tell them apart: this one is a pill on --surface-alt, the
 * header's is a --radius-sm box on white.
 *
 * As the reader types it suggests the five best articles (the last word is
 * treated as unfinished), so most questions are answered before Enter: arrow
 * keys move through them, Enter opens the highlighted one or, with none
 * highlighted, the full results page; Escape closes the list. Built as an ARIA
 * combobox so screen readers announce the options.
 *
 * `variant`: "hero" on /help, "compact" in page headers. `category` limits the
 * search to one category (the category page's own box).
 */
const HelpSearch = ({ history, variant = "hero", category = null, placeholder, initialValue = "" }) => {
  const [query, setQuery] = useState(initialValue);
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const rootRef = useRef(null);
  const idRef = useRef(null);
  if (idRef.current === null) {
    instanceCount += 1;
    idRef.current = `hc-suggest-${instanceCount}`;
  }
  const listId = idRef.current;
  const categorySlug = category ? category.slug : null;

  useEffect(() => {
    const keyword = query.trim();
    if (keyword.length < 2) {
      setSuggestions([]);
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setSuggestions(searchArticles(keyword, categorySlug, { limit: SUGGESTION_LIMIT, prefix: true }));
      setActiveIndex(-1);
    }, SUGGEST_AFTER_MS);
    return () => window.clearTimeout(timer);
  }, [query, categorySlug]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const closeOutside = e => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", closeOutside);
    return () => document.removeEventListener("mousedown", closeOutside);
  }, [isOpen]);

  const keyword = query.trim();
  const showList = isOpen && keyword.length >= 2;

  const openResults = () => {
    if (keyword === "") return;

    const params = new URLSearchParams({ keyword });
    if (categorySlug) params.set("category", categorySlug);
    setIsOpen(false);
    history.push(`/search?${params.toString()}`);
  };

  const openArticle = article => {
    setIsOpen(false);
    history.push(`/${article.id}`);
  };

  const submit = e => {
    e.preventDefault();
    if (showList && activeIndex >= 0 && activeIndex < suggestions.length) openArticle(suggestions[activeIndex].article);
    else openResults();
  };

  const onKeyDown = e => {
    if (e.key === "Escape") {
      setIsOpen(false);
      setActiveIndex(-1);
      return;
    }
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    if (suggestions.length === 0) return;

    e.preventDefault();
    setIsOpen(true);
    setActiveIndex(index => {
      if (e.key === "ArrowDown") return index + 1 >= suggestions.length ? -1 : index + 1;
      return index - 1 < -1 ? suggestions.length - 1 : index - 1;
    });
  };

  const label = category ? `Cari di kategori ${category.title}` : "Cari jawaban di Help Center";

  return (
    <div className={`hc-search-wrap hc-search-wrap--${variant}`} ref={rootRef}>
      <form className={`hc-search hc-search--${variant}`} role="search" onSubmit={submit}>
        <span className="hc-search__icon">
          <SearchIcon size={variant === "hero" ? 20 : 18} />
        </span>
        <input
          className="hc-search__input"
          type="search"
          name="keyword"
          value={query}
          onChange={e => {
            setQuery(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={placeholder || label}
          aria-label={label}
          autoComplete="off"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={showList}
          aria-controls={listId}
          aria-activedescendant={showList && activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined}
        />
      </form>

      {showList ? (
        <div className="hc-suggest">
          {suggestions.length > 0 ? (
            <ul className="hc-suggest__list" id={listId} role="listbox" aria-label="Saran artikel">
              {suggestions.map(({ article }, index) => (
                <li
                  key={article.id}
                  id={`${listId}-${index}`}
                  role="option"
                  aria-selected={index === activeIndex}
                  className={`hc-suggest__item${index === activeIndex ? " is-active" : ""}`}
                  // Keep focus in the input while choosing, so the list stays put.
                  onMouseDown={e => e.preventDefault()}
                  onClick={() => openArticle(article)}
                  onMouseEnter={() => setActiveIndex(index)}
                >
                  <span className="hc-suggest__text">
                    <span className="hc-suggest__title">{article.title}</span>
                    <span className="hc-suggest__cat">{article.category.title}</span>
                  </span>
                  <span className="hc-suggest__arrow">
                    <ArrowRightIcon size={14} />
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="hc-suggest__empty" id={listId}>
              Belum ada artikel yang cocok. Tekan Enter untuk cari lebih luas.
            </p>
          )}
          <button
            type="button"
            className="hc-suggest__all"
            onMouseDown={e => e.preventDefault()}
            onClick={openResults}
          >
            Lihat semua hasil untuk &ldquo;{keyword}&rdquo;
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default withRouter(HelpSearch);
